import dayjs from 'dayjs';
import { useState } from 'react';
import { styled } from 'styled-components';

interface Props {
  selected: string;
  setSelected: (select: string) => void;
  minDate?: string;
  maxDate?: string;
}

/**
 *
 * @param minDate 선택 가능한 최소 날짜(이 이전은 선택 불가)
 * @param maxDate 선택 가능한 최대 날짜(이 이후는 선택 불가)
 * @param selected
 * @param setSelected
 * @returns
 */
function DatePicker({ selected, setSelected, minDate, maxDate }: Props) {
  const [viewMode, setViewMode] = useState<'calendar' | 'yearMonth'>(
    'calendar',
  );
  const [current, setCurrent] = useState(
    dayjs(selected || minDate || maxDate || undefined),
  );
  // const [selected, setSelected] = useState(dayjs().format('YYYY-MM-DD'));

  const toggleViewMode = () => {
    setViewMode((prev) => (prev === 'calendar' ? 'yearMonth' : 'calendar'));
  };
  const dateList = [];
  for (let year = 2023; year <= 2026; year++) {
    for (let month = 1; month <= 12; month++) {
      dateList.push({ year, month });
    }
  }
  const startOfMonth = current.startOf('month').day();
  const daysInMonth = current.daysInMonth();
  const dates = Array(startOfMonth)
    .fill(null)
    .concat(Array.from({ length: daysInMonth }, (_, i) => i + 1));
  const years = Array.from({ length: 21 }, (_, i) => 2015 + i);
  const months = Array.from({ length: 12 }, (_, i) => i + 1);

  const min = minDate ? dayjs(minDate) : null;
  const max = maxDate ? dayjs(maxDate) : null;

  const isOutOfRange = (d: dayjs.Dayjs) => {
    if (min && d.isBefore(min, 'day')) return true;
    if (max && d.isAfter(max, 'day')) return true;
    return false;
  };

  const handlePrevMonth = () => {
    const prev = current.subtract(1, 'month');
    // prev 달이 전부 범위 밖인 경우 막기 (달의 마지막 날과 min 비교)
    if (min && prev.endOf('month').isBefore(min, 'day')) return;
    setCurrent(prev);
  };

  const handleNextMonth = () => {
    const next = current.add(1, 'month');
    // next 달이 전부 범위 밖인 경우 막기 (달의 첫 날과 max 비교)
    if (max && next.startOf('month').isAfter(max, 'day')) return;
    setCurrent(next);
  };

  return (
    <CalendarBox>
      <Header>
        <MonthBox onClick={toggleViewMode}>
          <span>{dayjs(current).format('YYYY년')}</span>
          <span>{dayjs(current).format('MM월')}</span>
          <img
            src="assets/images/arrow/arrow-down-gray.svg"
            alt=""
            width={16}
            height={16}
            className={viewMode === 'yearMonth' ? 'rotated' : ''}
          />
        </MonthBox>
        {viewMode === 'calendar' ? (
          <MonthMoveButtonWrapper>
            <img
              src="assets/images/arrow/gray_arrow_left.svg"
              alt=""
              onClick={handlePrevMonth}
            />
            <img
              src="assets/images/arrow/gray_arrow_right.svg"
              alt=""
              onClick={handleNextMonth}
            />
          </MonthMoveButtonWrapper>
        ) : (
          <YearMonthHeader onClick={toggleViewMode}>
            날짜 선택
            <img
              src="assets/images/arrow/arrow-down-gray.svg"
              alt=""
              width={16}
              height={16}
            />
          </YearMonthHeader>
        )}
      </Header>
      {viewMode === 'calendar' ? (
        <Dates>
          {dates.map((day, idx) => {
            if (!day) return <div key={idx} />;
            const date = current.date(day);
            const disabled = isOutOfRange(date);
            const isSelected = selected === date.format('YYYY-MM-DD');

            return (
              <div
                key={idx}
                className={`${isSelected ? 'selected' : ''} ${disabled ? 'disabled' : ''}`}
                onClick={() => {
                  if (disabled) return;
                  setSelected(date.format('YYYY-MM-DD'));
                }}
              >
                {day}
              </div>
            );
          })}
        </Dates>
      ) : (
        <YearMonthListBox>
          <YearMonthList>
            {years.map((year) => {
              const start = current.year(year).startOf('year');
              const end = current.year(year).endOf('year');
              const yearDisabled =
                (min && end.isBefore(min, 'day')) ||
                (max && start.isAfter(max, 'day'));

              const isSelected = year === current.year();
              return (
                <YearMonth
                  key={year}
                  className={`${isSelected ? 'selected' : ''} ${yearDisabled ? 'disabled' : ''}`}
                  onClick={() => {
                    if (yearDisabled) return;
                    const newDate = current.year(year).startOf('month');
                    setCurrent(newDate);
                    if (!isOutOfRange(newDate))
                      setSelected(newDate.format('YYYY-MM-DD'));
                  }}
                  ref={(el) => {
                    if (isSelected && el) {
                      el.scrollIntoView({
                        block: 'center',
                        behavior: 'smooth',
                      });
                    }
                  }}
                >
                  {year}년
                </YearMonth>
              );
            })}
          </YearMonthList>
          <YearMonthList>
            {months.map((month) => {
              const start = current.month(month - 1).startOf('month');
              const end = current.month(month - 1).endOf('month');
              const monthDisabled =
                (min && end.isBefore(min, 'day')) ||
                (max && start.isAfter(max, 'day'));

              const isSelected = month === current.month() + 1;

              return (
                <YearMonth
                  key={month}
                  className={`${isSelected ? 'selected' : ''} ${monthDisabled ? 'disabled' : ''}`}
                  onClick={() => {
                    if (monthDisabled) return;
                    const newDate = current.month(month - 1).startOf('month');
                    setCurrent(newDate);
                    if (!isOutOfRange(newDate)) {
                      setSelected(newDate.format('YYYY-MM-DD'));
                    }
                  }}
                  ref={(el) => {
                    if (isSelected && el) {
                      el.scrollIntoView({
                        block: 'center',
                        behavior: 'smooth',
                      });
                    }
                  }}
                >
                  {String(month).padStart(2, '0')}월
                </YearMonth>
              );
            })}
          </YearMonthList>
        </YearMonthListBox>
      )}
    </CalendarBox>
  );
}

export default DatePicker;

const CalendarBox = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const MonthBox = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 4px;
  cursor: pointer;

  font-weight: 700;
  font-size: 14px;
  line-height: 145%;
  letter-spacing: -2%;
  color: #222222;

  img.rotated {
    transform: rotate(180deg);
  }
`;

const MonthMoveButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  & > img {
    cursor: pointer;
  }

  & > img[aria-disabled='true'] {
    opacity: 0.3;
    pointer-events: none;
    cursor: not-allowed;
  }
`;

const Dates = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  text-align: center;
  div {
    padding-top: 8px;
    padding-right: 8px;
    padding-bottom: 16px;
    padding-left: 8px;
    cursor: pointer;
    border-radius: 8px;

    font-weight: 500;
    font-size: 14px;
    line-height: 145%;
    letter-spacing: -2%;
    text-align: center;
    color: #616161;

    &:hover {
      background: #eaf2fe;
    }
  }
  .selected {
    background: #eaf2fe;
    color: #008aff;
  }

  .disabled {
    opacity: 0.4;
    pointer-events: none;
    cursor: not-allowed;
  }
`;

const YearMonthHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 6px 8px;

  font-weight: 500;
  font-size: 14px;
  line-height: 145%;
  letter-spacing: -2%;
  color: #616161;

  img {
    transform: rotate(-90deg);
  }
`;

const YearMonthListBox = styled.div`
  display: flex;
  flex-direction: row;
  height: 222px;
  overflow-y: hidden;
  gap: 8px;
  justify-content: center;
  align-items: center;
`;

const YearMonthList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  height: 100%;
  overflow-y: auto;
  scroll-snap-type: y mandatory;
  align-items: center;
  padding: 8px 0;

  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const YearMonth = styled.div`
  padding: 8px;
  font-weight: 500;
  font-size: 14px;
  line-height: 145%;
  letter-spacing: -2%;
  color: #616161;

  cursor: pointer;
  scroll-snap-align: center;

  &.selected {
    color: #008aff;
    border-bottom: 1px solid #e1e2e4;
    border-top: 1px solid #e1e2e4;
  }

  &.disabled {
    opacity: 0.4;
    pointer-events: none;
    cursor: not-allowed;
  }
`;
